import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  ProgressBar,
  Row
} from "react-bootstrap";
import {
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { load } from 'recaptcha-v3';
import {
  getAllSurveyResponses,
  getPollResponse,
  getSurveyById,
  getSurveySectionsDetails,
  insertSurveyResponse,
  insertSurveyResponseViewDateTime
} from "../../utils/ApiUrl";
import {
  axiosPost
} from "../../utils/AxiosHelper";
import {
  convertToUTC,
  decrypt,
  getCurrentDate,
  hhmmToSeconds,
} from "../../utils/common";
import "./DetailSurveyForm.scss";
import {
  DetailedInfo,
  SurveyById,
  SurveyResponse,
  SurveySection,
} from "./types";
import RecursiveDetailSurveyForm from "./RecursiveDetailSurveyForm";
import Loader from "../loader/Loader";
import WelcomePage from "../pages/WelcomePage";
import savedraftico from "../../assets/images/save-draft.svg";
import dashboardico from "../../assets/images/gotodashboard.svg";
import { ToastTypeCode, showToast, ToastPosition } from "../../utils/Constant";
import PollView from "./PollView";
import PollResult from "./PollResult";

const DetailSurveyForm: React.FC = () => {
  const [applyValidation, setApplyValidation] = useState(true);
  const [showSubmitButton, setShowSubmitButton] = useState(true);
  const [showProgress, setShowProgress] = useState(true);
  const [responseStartDate, setResponseStartDate] = useState<Date | null>(null);
  const [responseEndDate, setResponseEndDate] = useState<Date | null>(null);
  let { id, assignUserId, surveyIdFromParams } = useParams();
  const [currentCount, setCurrentCount] = useState<number>(0);

  const { surveyFormsId, userssId, surveysId } = useParams<{
    surveyFormsId: string;
    userssId: string;
    surveysId: string;
  }>();

  const methods = useForm();
  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger,
    setValue,
    watch,
    getValues,
    register,
  } = methods;

  const [surveySections, setSurveySections] = useState<SurveySection[]>([]);
  const [surveyFormTitle, setSurveyFormTitle] = useState<string>("");
  const [thankYouMessage, setThankYouMessage] = useState<string>("");
  const [surveyFormInstructions, setSurveyFormInstructions] =
    useState<string>("");
  const [surveyDataById, setSurveyDataById] = useState<SurveyById[]>([]);
  const navigate = useNavigate();
  const [surveysFromId, setSurveysFromId] = useState<any>();
  const [surveysAssignmentId, setSurveysAssignmentId] = useState<number>();
  const [userId, setUserId] = useState<any>();
  const [surveyId, setSurveyId] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(0);
  const [welcomePageDemo, setWelcomePageDemo] = useState(false);
  const [pollResultView, setPollResultView] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const [isTimeOver, setIsTimeOver] = useState<boolean>(false);
  const [showModal, setShowModal] = useState(false);
  const [durationInMinutes, setDurationInMinutes] = useState<number>();
  const [alertBeforeTime, setAlertBeforeTime] = useState<number>();
  const [formType, setFormType] = useState<number>(1);
  const [timeLeft, setTimeLeft] = useState<number>();
  const [submitButtonClicked, setSubmitButtonClicked] = useState<number>(1);
  const [timeDifferenceAgainLogin, setTimeDifferenceAgainLogin] =
    useState<number>(0);
  const [isCaptchaEnabled, setIsCaptchaEnabled] = useState<boolean>(false);
  const [totalquestions, setTotalQuestions] = useState<number>(0);

  const handleCaptcha = async (): Promise<string | null> => {
    try {
      const recaptcha = await load(
        process.env.REACT_APP_RECAPTCHA_SITE_KEY as string
      );
      const resultToken = await recaptcha.execute("formSubmission");
      return resultToken;
    } catch (error) {
      console.error("reCAPTCHA error:", error);
      return null;
    }
  };

  useEffect(() => {
    fetchData();
  }, [id, assignUserId, surveyIdFromParams]);

  useEffect(() => {
    if (!welcomePageDemo && showSubmitButton && formType === 1) return;
    if (
      formType === 2 &&
      welcomePageDemo &&
      showSubmitButton === true &&
      userId &&
      surveyId
    ) {
      const timer = setInterval(() => {
        setTimeLeft((prevTime = 0) => {
          if (prevTime <= 1) {
            setIsTimeOver(true);
            setValue("TimeOver", true);
            clearInterval(timer);
            handleSubmit(handleFinalSubmit)();
            navigate("/time-over");
            return 0;
          }
          if (alertBeforeTime !== undefined && prevTime === alertBeforeTime) {
            showToast(
              `Hurry up! Only ${alertBeforeTime / 60} minutes left.`,
              ToastTypeCode?.ERROR
            );
          }
          return prevTime - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [navigate, welcomePageDemo, formType]);

  useEffect(() => {
    if (welcomePageDemo === true) {
      const response: any = axiosPost(insertSurveyResponseViewDateTime, {
        SurveyId: surveyId,
      });
      setResponseStartDate(new Date());
    }
  }, [welcomePageDemo]);

  const handleShowModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handlePreview = () => {
    setIsDraft(true);
    setShowModal(false);
  };

  const fetchData = async () => {
    if (id) {
      const survey = await decrypt(id);
      setSurveysFromId(survey);
    }

    if (assignUserId) {
      const user = await decrypt(assignUserId);
      setUserId(user);
    }

    if (!assignUserId) {
      setApplyValidation(false);
    }

    if (surveyIdFromParams) {
      fetchSurveyFromId(surveyIdFromParams);
      const surveysid = await decrypt(surveyIdFromParams);
      setSurveyId(surveysid);
    }
  };

  let surveyFormId: any = surveysFromId;
  let usersId: any = userId;
  let surveyid: any = surveyId;

  const fetchSurveyFromId = async (id: any) => {
    setLoading(true);
    try {
      const surveysid = await decrypt(surveyIdFromParams);
      const response: any = await axiosPost(getSurveyById, {
        SurveyId: surveysid,
      });
      if (response?.data?.Status === 200) {
        const currentDateWhole = new Date(convertToUTC(getCurrentDate()));
        const currentDate = convertToUTC(getCurrentDate()).split("T")[0];
        const startDate = convertToUTC(
          response?.data?.ExtraData?.StartDate
        ).split("T")[0];
        const targetCompletionDate = convertToUTC(
          response?.data?.ExtraData?.TargetCompletionDate
        ).split("T")[0];
        setSurveysAssignmentId(response?.data?.ExtraData?.SurveyAssignmentId);
        let utcDateTime = response?.data?.ExtraData?.ViewDateTime;
        if (typeof utcDateTime === "string") {
          utcDateTime = new Date(utcDateTime);
        }

        if (utcDateTime instanceof Date) {
          const timeDifferenceInMs =
            currentDateWhole.getTime() - utcDateTime.getTime();
          let timeDifferenceInMinutes = Math.floor(timeDifferenceInMs / 1000);
          setTimeDifferenceAgainLogin(timeDifferenceInMinutes);
        } else {
          console.error("Invalid UTC DateTime:", utcDateTime);
        }

        if (response.data.ExtraData.Status === 2) {
          //navigate("/form-submitted");
        } else if (
          currentDate < startDate ||
          targetCompletionDate < currentDate
        ) {
          navigate("/form-not-started");
        }
        setSurveyDataById(response?.data?.ExtraData);
      } else {
        console.error("Error fetching survey responses");
      }
    } catch (error) {
      console.error("Error fetching survey responses", error);
    } finally {
      setLoading(false);
    }
  };

  const insertArrayAtIndex = <T,>(
    targetArray: T[],
    insertArray: T[],
    index: number
  ): T[] => {
    const resultArray = [...targetArray];
    resultArray?.splice(index, 0, ...insertArray);
    return resultArray;
  };

  const getOrderedDetailInfoRecursive = async (
    detailinfo: DetailedInfo[],
    orderedDetailinfo: DetailedInfo[],
    surveyBranchLogic: any[]
  ): Promise<DetailedInfo[]> => {
    if (!detailinfo || detailinfo?.length === 0) {
      return orderedDetailinfo;
    }
    // Array to hold child questions for the current level
    let childQuestions: DetailedInfo[] = [];

    if (orderedDetailinfo && orderedDetailinfo?.length > 0) {
      for (const info of orderedDetailinfo) {
        // Find child questions for the current parent question
        childQuestions = detailinfo?.filter((item) =>
          surveyBranchLogic
            ?.filter((inneritem) => inneritem.QuestionId === info.QuestionId)
            ?.map((inneritem) => inneritem.NextSurveyQuestionId)
            ?.includes(item.QuestionId)
        );

        // Find index of current parent question in orderedDetailinfo
        const Qindex = orderedDetailinfo?.findIndex(
          (item) => item.QuestionId === info.QuestionId
        );

        // Insert child questions after current parent question in orderedDetailinfo
        orderedDetailinfo = insertArrayAtIndex(
          orderedDetailinfo,
          childQuestions,
          Qindex + 1
        );

        // Remove child questions from detailinfo
        detailinfo = detailinfo?.filter(
          (item) => !childQuestions?.includes(item)
        );
      }
    } else {
      // Get parent questions
      const MostParentQuestion: DetailedInfo[] = detailinfo?.filter(
        (item1) =>
          !surveyBranchLogic?.some(
            (item2) => item2.NextSurveyQuestionId === item1.QuestionId
          )
      );
      orderedDetailinfo = [...orderedDetailinfo, ...MostParentQuestion];
      detailinfo = detailinfo?.filter(
        (item) => !MostParentQuestion?.includes(item)
      );
    }

    // Recursively call getOrderedDetailInfoRecursive with updated parameters
    return await getOrderedDetailInfoRecursive(
      detailinfo,
      orderedDetailinfo,
      surveyBranchLogic
    );
  };

  const fetchSurveySections = async () => {
    let userIddecrypted = await decrypt(assignUserId);
    setLoading(true);
    try {
      const response: any = await axiosPost(getSurveySectionsDetails, {
        SurveyFormId: surveyFormId,
        SearchText: "",
        IsActive: true,
        CurrentPageNo: 1,
        PageSize: 20,
        SortDir: "ASC",
        SortBy: "SurveyFormId",
        usersId: userIddecrypted,
      });
      if (response?.data?.Status === 200) {
        const surveyBranchLogic =
          response?.data?.ExtraData?.surveyQuestionBranchLogicLists;
        const sections = response?.data?.ExtraData?.surveySectionsLists;
        for (const s of sections) {
          let orderedDetailinfo: DetailedInfo[] =
            await getOrderedDetailInfoRecursive(
              s?.DetailedInfo,
              [],
              surveyBranchLogic
            );
          s.DetailedInfo = orderedDetailinfo;
        }
        const allSectionsTotalQuestions = sections.reduce(
          (sum: number, { CombinedCount = 0 }: DetailedInfo) => sum + CombinedCount,
          0
        );
        if (sections[0]?.TimeLimit) {
          setAlertBeforeTime(hhmmToSeconds(sections[0]?.AlertBeforeTime));
          setDurationInMinutes(hhmmToSeconds(sections[0]?.TimeLimit));
          setFormType(sections[0]?.FormType);
        }
        setSurveySections(sections);
        setSurveyFormTitle(sections[0]?.SurveyFormTitle || "");
        setThankYouMessage(sections[0]?.ThankYouMessage);
        setIsCaptchaEnabled(sections[0]?.IsActiveInConfigDetail);
        setTotalQuestions(allSectionsTotalQuestions);
        setSurveyFormInstructions(sections[0]?.SurveyFormInstructions);
        if (assignUserId) {
          const userIdProp = await decrypt(assignUserId);
          fetchSurveyResponses(userIdProp);
        }
      } else {
        console.error("Error fetching survey sections");
      }
    } catch (error) {
      console.error("Error fetching survey sections", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSurveyResponses = async (userIdProp?: number) => {
    
    setLoading(true);

    try {
      const surveysid = await decrypt(surveyIdFromParams);
      const response: any = await axiosPost(getAllSurveyResponses, {
        SurveyId: surveysid,
        SearchText: "",
        IsActive: true,
        CurrentPageNo: 1,
        PageSize: 100,
        SortDir: "ASC",
        SortBy: "SurveyResponseId",
        AssignedUserId: userIdProp,
        usersId: userIdProp,
      });

      if (response?.data?.Status === 200) {
        const surveyResponses: SurveyResponse[] = response?.data?.ExtraData;
        if (surveyResponses?.length > 0) {
          setShowSubmitButton(false);
          setWelcomePageDemo(true);

          const hasDraft = surveyResponses.some((res) => res.IsDraft);
          if (hasDraft) {
            setIsDraft(true);
            setShowSubmitButton(true);
            setShowProgress(true);
          }

          surveyResponses?.forEach((surveyResponse) => {
            const {
              QuestionId,
              SelectedResponseText,
              SelectedResponseValue,
              SelectedStarResponseValue,
              FieldId,
              IsDraft,
            } = surveyResponse;

            // Handle matrix fields
            if (FieldId) {
              setValue(`matrix_${FieldId}`, SelectedResponseValue);
            }

            // Handle general questions
            if (SelectedResponseText) {
              setValue(`question_${QuestionId}`, SelectedResponseText);
            } else if (SelectedResponseValue) {
              if (SelectedResponseValue?.includes(",")) {
                setValue(
                  `question_${QuestionId}`,
                  SelectedResponseValue.split(",") // Split for multi-select values
                );
              } else {
                setValue(`question_${QuestionId}`, SelectedResponseValue); // Single value
              }
            } else if (SelectedStarResponseValue) {
              setValue(`question_${QuestionId}`, SelectedStarResponseValue);
            }

            // Track draft status
            if (IsDraft) {
              setIsDraft(true);
            }
          });
        }
      } else {
        console.error("Error fetching responses");
      }
    } catch (error) {
      console.error("Error fetching responses", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (surveyFormId) {
      fetchSurveySections();
    }
    if (!assignUserId || assignUserId === undefined) {
      setApplyValidation(false);

      setShowProgress(false);
    }
  }, [surveyFormId]);

  const getSubmitPayload = async (
    surveySections: SurveySection[],
    data: Record<string, any>,
    responsedata: any[]
  ): Promise<any[]> => {
    if (!responsedata) {
      responsedata = [];
    }

    for (const section of surveySections ?? []) {
      let sectionId = section.SurveySectionId;

      if (section?.DetailedInfo) {
        for (const question of section?.DetailedInfo ?? []) {
          let questionId = question.QuestionId;

          const responseId = data[`question_${questionId}`];
          if (question?.SurveyQuestionResponseDetails) {
            for (const questionResponseDetail of question?.SurveyQuestionResponseDetails ?? []) {
              let questionResponseId =
                questionResponseDetail.QuestionResponseId;
              let responseTypeName = questionResponseDetail.ResponseTypeName;

              let selectedResponseText = "";
              let selectedResponseValue = "";
              let selectedStarResponseValue = "";


              if (
                responseTypeName === "CheckBox" ||
                responseTypeName === "Multi-Select DropDown"
              ) {
                selectedResponseValue = Array.isArray(responseId) ? responseId.join(",") : responseId ?? "";
              } else if (
                responseTypeName === "DropDown" ||
                responseTypeName === "Radio Button"
              ) {
                selectedResponseValue = responseId;
              } else if (responseTypeName == "TextBox") {
                selectedResponseText = responseId;
              } else if (
                responseTypeName === "3 Star Rating" ||
                responseTypeName === "5 Star Rating" ||
                responseTypeName === "10 Star Rating" ||
                responseTypeName === "3 Star Rating With N/A" ||
                responseTypeName === "5 Star Rating With N/A" ||
                responseTypeName === "10 Star Rating With N/A" ||
                responseTypeName === "Smiley" ||
                responseTypeName === "NPS" ||
                responseTypeName === "ThumbsUpDown"
              ) {
                selectedStarResponseValue = responseId?.toString();
              } else {
              }

              // Handle matrix response type
              if (responseTypeName === "MatrixTypeResponse") {
                // Iterate through the data to extract matrix keys
                Object.entries(data).forEach(([key, value]) => {
                  if (key.startsWith("matrix_") && value) {
                    const [, fieldId, optionId] = key.split("_"); // Extract fieldId and optionId from key

                    // Create the matrix response object
                    const matrixResponseObj = {
                      SurveySectionId: sectionId,
                      QuestionId: questionId,
                      QuestionResponseId: questionResponseId,
                      SelectedResponseText: "",
                      SelectedResponseValue: value.toString(), // Assign the matrix value
                      SelectedStarResponseValue: "",
                      FieldId: parseInt(fieldId, 10), // Convert fieldId to a number
                      OptionId: parseInt(optionId ?? 0, 10), // Convert optionId to a number
                    };

                    responsedata.push(matrixResponseObj);
                  }
                });
              } else {
                // Handle non-matrix response types
                const questionKey = `question_${questionId}`;
                const responseValue = data[questionKey];

                if (responseValue) {
                  const nonMatrixResponseObj = {
                    SurveySectionId: sectionId,
                    QuestionId: questionId,
                    QuestionResponseId: questionResponseId,
                    SelectedResponseText: selectedResponseText,
                    SelectedResponseValue: selectedResponseValue,
                    SelectedStarResponseValue: selectedStarResponseValue,
                    FieldId: 0,
                    OptionId: 0,
                  };

                  responsedata.push(nonMatrixResponseObj);
                }
              }
            }
          }
        }
      }

      if (section.SurveySubSection) {
        await getSubmitPayload(section.SurveySubSection, data, responsedata);
      }
    }

    return responsedata;
  };

  const watchedValues = watch();

  const calculateFinalCount = (counts: Record<string, number>) => {
    return Object.values(counts).reduce((total, value) => {
      return total + (value > 0 ? 1 : 0);
    }, 0);
  };

  useEffect(() => {
    const selectedCounts = Object.fromEntries(
      Object.entries(watchedValues)?.map(([key, value]) => [
        key,
        Array.isArray(value) ? value?.length : value ? 1 : 0,
      ])
    );

    const finalCount = calculateFinalCount(selectedCounts);


    setCurrentCount(finalCount);
  }, [watchedValues]);



  const getVariant = (percentage: number) => {
    if (percentage < 50) return "danger";
    if (percentage < 80) return "warning";
    return "success";
  };

  const handleFinalSubmit: SubmitHandler<FieldValues> = async (data) => {
    if (!assignUserId) {
      return;
    }
    let token = null;

    if (isCaptchaEnabled) {
      try {
        token = await handleCaptcha();

        if (!token) {
          showToast(
            "CAPTCHA validation failed. Please try again.",
            ToastTypeCode?.ERROR
          );
          return;
        }
      } catch (error) {
        showToast(
          "An error occurred during reCAPTCHA verification.",
          ToastTypeCode?.ERROR
        );
        return;
      }
    }

    let questionAnsList: SurveySection[] = await getSubmitPayload(
      surveySections,
      data,
      []
    );

    if (!isDraft && questionAnsList?.length === 0) {
      showToast("No data to Insert", ToastTypeCode?.ERROR);
      return;
    }

    let userIddecrypted = await decrypt(assignUserId);
    const payload = {
      SurveyId: surveyid,
      SurveyFormId: surveyFormId,
      AssignUserID: usersId ? usersId : null,
      questionAnsList: questionAnsList,
      usersId: userIddecrypted,
      IsDraft: false,
      ResponseEndDate: convertToUTC(getCurrentDate()),
      ResponseStartDate: responseStartDate,
      IsTimeOver: data?.TimeOver ? data?.TimeOver : false,
      recaptchaToken: token,
    };

    setLoading(true);

    try {
      const response: any = await axiosPost(insertSurveyResponse, payload);
      if (response.data?.Status !== 200) {
        showToast(response?.data?.ErrorMessage, ToastTypeCode?.ERROR);
      } else {
          showToast("Form submitted successfully", ToastTypeCode?.SUCCESS);
          if (!data?.TimeOver) {
            if (formType === 3) {
              setPollResultView(true);
            } else {
              navigate(`/thankyou`, {
                state: {
                  message: thankYouMessage,
                },
              });
            }
          } 
      }
    } catch (error) {
      showToast("Error submitting the Form", ToastTypeCode?.ERROR);
    } finally {
      setLoading(false);
    }
  };

  

  const handleDraftSubmit = async (watchAllFields: any, isDraft: boolean) => {
    if (!assignUserId) {
      return;
    }

    let token = null;

    if (isCaptchaEnabled) {
      try {
        token = await handleCaptcha();

        if (!token) {
          showToast(
            "CAPTCHA validation failed. Please try again.",
            ToastTypeCode?.ERROR
          );
          return;
        }
      } catch (error) {
        showToast(
          "An error occurred during reCAPTCHA verification.",
          ToastTypeCode?.ERROR
        );
        return;
      }
    }

    let questionAnsList: SurveySection[] = await getSubmitPayload(
      surveySections,
      watchAllFields,
      []
    );

    if (!isDraft && questionAnsList?.length === 0) {
      showToast("No data to Insert", ToastTypeCode?.ERROR);
      return;
    }

    let userIddecrypted = await decrypt(assignUserId);

    const payload = {
      SurveyId: surveyid,
      SurveyFormId: surveyFormId,
      AssignUserID: usersId ? usersId : null,
      questionAnsList: questionAnsList,
      usersId: userIddecrypted,
      IsDraft: isDraft,
      recaptchaToken: token,
    };

    setLoading(true);
    try {
      const response: any = await axiosPost(insertSurveyResponse, payload);
      if (response?.data.Status !== 200) {
        showToast(response?.data?.ErrorMessage, ToastTypeCode?.ERROR);
      } else {
        if (isDraft) {
          showToast("Draft saved successfully", ToastTypeCode?.SUCCESS);
          navigate("/dashboard");
        } else {
          showToast("Form submitted successfully", ToastTypeCode?.SUCCESS);
          navigate(`/thankyou`, {
            state: {
              message: thankYouMessage,
            },
          });
        }
      }
    } catch (error) {
      showToast("Error submitting the Form", ToastTypeCode?.ERROR);
    } finally {
      setLoading(false);
      setIsDraft(false);
    }
  };

  const generatePdf = async () => {
    const pdf = new jsPDF("p", "mm", "a4");
    const pageHeight = pdf.internal.pageSize.height;
    const pageWidth = pdf.internal.pageSize.width;

    for (let i = 0; i < surveySections?.length; i++) {
      setCurrentRecord(i);
      await new Promise((resolve) => setTimeout(resolve, 100));

      const pageContent = document.querySelector(".surveypreview-wrapper2") as HTMLElement | null;
      if (!pageContent) {
        console.error("Content not found!");
        continue;
      }

      pageContent.style.maxWidth = "800px";
      pageContent.style.margin = "10px";

      const canvas = await html2canvas(pageContent, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const imgWidth = pageWidth;
      const imgHeight = (canvas?.height * imgWidth) / canvas?.width;
      const totalHeight = canvas?.height;

      let currentY = 0;

      while (currentY < totalHeight) {
        const sliceCanvas = document.createElement("canvas");
        const sliceContext = sliceCanvas?.getContext("2d");

        const sliceHeight = Math.min(totalHeight - currentY, pageHeight * (canvas?.height / imgHeight));
        sliceCanvas.width = canvas?.width;
        sliceCanvas.height = sliceHeight;

        if (sliceContext) {
          sliceContext?.drawImage(
            canvas,
            0,
            currentY,
            canvas?.width,
            sliceHeight,
            0,
            0,
            canvas?.width,
            sliceHeight
          );
        }

        const sliceImgData = sliceCanvas?.toDataURL("image/png");
        const slicePdfHeight = (sliceHeight * imgWidth) / canvas?.width;

        if (currentY === 0 && i === 0) {
          pdf.addImage(sliceImgData, "PNG", 0, 0, imgWidth, slicePdfHeight);
        } else {
          pdf.addPage();
          pdf.addImage(sliceImgData, "PNG", 0, 0, imgWidth, slicePdfHeight);
        }

        currentY += sliceHeight;
      }
      pageContent.style.maxWidth = "";
      pageContent.style.margin = "";
    }
    pdf.save(`${surveyFormTitle}.pdf`);
    window.location.reload();
  };

  const fetchPollLiveResult = async () => {
    const payload = {
      SurveyAssignmentId: surveysAssignmentId,
      SurveyFormId: surveyFormId,
    };
    setLoading(true);
    try {
      const response: any = await axiosPost(getPollResponse, payload);
      if (response?.data?.Status !== 200) {
        showToast(response?.data?.ErrorMessage, ToastTypeCode?.ERROR);
      } else {
      }
    } catch (error) {
      showToast("Error submitting the Form", ToastTypeCode?.ERROR);
    } finally {
      setLoading(false);
    }

    setPollResultView(true);
  };

  const handleNext = async () => {
    if (userId && surveyId) {
      const valid = await methods.trigger();
      if (valid) {
        setCurrentRecord((prevStep) => prevStep + 1);
      }
    } else {
      setCurrentRecord((prevStep) => prevStep + 1);
    }
  };

  useEffect(() => {
    if (durationInMinutes !== undefined) {
      setTimeLeft(durationInMinutes - timeDifferenceAgainLogin);
    }
  }, [durationInMinutes, timeDifferenceAgainLogin]);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    return `${hours}:${minutes < 10 ? "0" : ""}${minutes}:${remainingSeconds < 10 ? "0" : ""
      }${remainingSeconds}`;
  };

  const progressPercentage =
    totalquestions > 0 ? Math.round((currentCount / totalquestions) * 100) : 0;

  return (
    <>
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Save</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Would you like to preview the form or submit it?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handlePreview}>
            Preview
          </Button>
          {currentRecord === surveySections?.length - 1 && showSubmitButton && (
            <Button
              variant="success"
              type="submit"
              className="mx-2"
              onClick={() => {
                setIsDraft(false);
                setShowModal(false);
                handleSubmit(handleFinalSubmit)();
                setSubmitButtonClicked(submitButtonClicked + 1);
              }}
            >
              Submit
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      {!welcomePageDemo && userId && surveyId ? (
        <WelcomePage
          SurveyFormTitle={surveyFormTitle}
          SurveyFromInstructions={surveyFormInstructions}
          setWelcomePageDemo={setWelcomePageDemo}
          formType={formType}
        />
      ) : (
        <div className="surveypreview-wrapper">
          <Container className="vh-100 align-items-center justify-content-center">
            <Card>
              <Card.Header>
                <div className="survey-title ">
                  <Row>
                    <Col lg={6}>
                      <h2 className="mb-0">{surveyFormTitle}</h2>
                    </Col>
                    <Col lg={6}>
                      <div className="d-flex justify-content-end question-actions">
                        {welcomePageDemo &&
                          userId &&
                          surveyId &&
                          showSubmitButton === true &&
                          formType === 2 && (
                            <div
                              className={`alert my-1 me-2 ${timeLeft !== undefined &&
                                  alertBeforeTime !== undefined &&
                                  timeLeft > alertBeforeTime
                                  ? "alert-success"
                                  : "alert-danger"
                                }`}
                            >
                              Time Left:{" "}
                              <span
                                className={`badge ms-2 ${timeLeft !== undefined &&
                                    alertBeforeTime !== undefined &&
                                    timeLeft > alertBeforeTime
                                    ? "badge-success"
                                    : "badge-danger"
                                  }`}
                              >
                                {timeLeft !== undefined
                                  ? formatTime(timeLeft)
                                  : "00:00:00"}
                              </span>
                            </div>
                          )}
                        {formType !== 3 && (
                          <>
                            { (!showProgress ||
                              showSubmitButton !== true) && (
                                <Button
                                type="button"
                                className="me-2"
                                onClick={generatePdf}
                              >
                                Generate PDF
                              </Button>
                              )
                            }
                            <Button
                              type="button"
                              variant="info"
                              className="mx-2"
                              onClick={() => {
                                setIsDraft(true);
                                const draftData = getValues();
                                handleDraftSubmit(draftData, true);
                              }}
                            >
                              <img
                                src={savedraftico}
                                className="me-2"
                                width="14"
                                height="14"
                                alt="Save Draft"
                              />
                              Save Draft
                            </Button>
                          </>
                        )}
                        <Button
                          className="btn-primary"
                          onClick={() => navigate("/dashboard")}
                        >
                          <img
                            src={dashboardico}
                            className="me-2"
                            width="16"
                            height="16"
                            alt="Dashboard"
                          />
                          Go To Dashboard
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Card.Header>
              <Card.Body>
                {showProgress && showSubmitButton === true && !pollResultView &&(
                  <>
                    <div className="progress-info">
                      <span>{currentCount}</span> /{" "}
                      <span>{totalquestions}</span>
                    </div>
                    <div className="progress-container">
                      <div className="col-md-12">
                        <div className="dashb-card">
                          <ProgressBar
                            striped
                            variant={getVariant(progressPercentage)}
                            now={progressPercentage}
                            label={`${progressPercentage}%`}
                            className="pg-block"
                          />
                          <p>Question Completion Rate</p>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <Row>
                  <Col>
                    {surveySections?.length > 0 && (
                      <>
                        {loading ? (
                          <Loader />
                        ) : formType === 3 ? (
                          <>
                            {pollResultView === false ? (
                              <>
                                <div className="surveypreview-wrapper2">
                                  <PollView
                                    register={register}
                                    control={control}
                                    errors={errors}
                                    watch={watch}
                                    RecursiveInnerSurveySections={surveySections[0]?.DetailedInfo}
                                    IsSubSection={false}
                                    showSubmitButton={showSubmitButton}
                                    setValue={setValue}
                                    currentRecord={currentRecord}
                                    questionCounter={1}
                                    isDraft={isDraft}
                                    submitButtonClicked={false}
                                  />
                                </div>
                                {showSubmitButton && (
                                  <Button
                                    variant="success"
                                    type="submit"
                                    className="mx-2"
                                    onClick={() => {
                                      setIsDraft(false);
                                      handleSubmit(handleFinalSubmit)();
                                      setSubmitButtonClicked(
                                        submitButtonClicked + 1
                                      );
                                    }}
                                    disabled={!applyValidation}
                                  >
                                    Submit
                                  </Button>
                                )}
                                <Button
                                  onClick={fetchPollLiveResult}
                                  className="mx-2 btn-primary"
                                >
                                  Live result
                                </Button>
                              </>
                            ) : (
                              <PollResult
                                setPollResultView={setPollResultView}
                                surveysAssignmentId={surveysAssignmentId}
                                surveyFormId={surveyFormId}
                              />
                            )}
                          </>
                        ) : (
                          <FormProvider {...methods}>
                            <Form onSubmit={handleSubmit(handleFinalSubmit)}>
                              <div className="surveypreview-wrapper2">
                                <RecursiveDetailSurveyForm
                                  register={register}
                                  control={control}
                                  errors={errors}
                                  watch={watch}
                                  recursiveSurveySections={
                                    surveySections[currentRecord]
                                  }
                                  IsSubSection={false}
                                  showSubmitButton={showSubmitButton}
                                  setValue={setValue}
                                  currentRecord={currentRecord}
                                  questionCounter={1}
                                  isDraft={isDraft}
                                  submitButtonClicked={submitButtonClicked}
                                />
                              </div>
                              <div className="text-center">
                                {currentRecord > 0 && (
                                  <Button
                                    variant="secondary"
                                    onClick={() =>
                                      setCurrentRecord(
                                        (prevStep) => prevStep - 1
                                      )
                                    }
                                    className="mx-2"
                                  >
                                    Previous
                                  </Button>
                                )}

                                {currentRecord < surveySections?.length - 1 && (
                                  <Button
                                    variant="primary"
                                    onClick={handleNext}
                                    className="mx-2"
                                  >
                                    Next
                                  </Button>
                                )}

                                {currentRecord === surveySections?.length - 1 && showProgress &&
                                  showSubmitButton === true && (
                                    <Button
                                      variant="primary"
                                      type="button"
                                      className="btn-primary mx-2"
                                      onClick={handleShowModal}
                                    >
                                      Save
                                    </Button>
                                  )}
                              </div>
                            </Form>
                          </FormProvider>
                        )}
                      </>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Container>
        </div>
      )}
    </>
  );
}
export default DetailSurveyForm;
